import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../../services/supabaseClient';

const UniversityDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [university, setUniversity] = useState(null);
    const [programs, setPrograms] = useState([]);
    const [intakes, setIntakes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                // Load University
                const { data: uni, error: uniError } = await supabase
                    .from('universities')
                    .select('*')
                    .eq('id', id)
                    .single();

                if (uniError) throw uniError;
                setUniversity(uni);

                // Load Programs
                const { data: progs, error: progError } = await supabase
                    .from('programs')
                    .select('*')
                    .eq('university_id', id)
                    .order('name');

                if (progError) throw progError;
                setPrograms(progs);

                // Load Intakes (for these programs)
                if (progs.length > 0) {
                    const progIds = progs.map(p => p.id);
                    const { data: ints, error: intError } = await supabase
                        .from('intakes')
                        .select('*, programs(name)')
                        .in('program_id', progIds)
                        .order('start_date', { ascending: true });

                    if (intError) throw intError;
                    setIntakes(ints);
                } else {
                    setIntakes([]);
                }

            } catch (e) {
                console.error(e);
                setError('Failed to load university details');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [id]);

    if (loading) return (
        <div className="min-h-screen bg-gray-900 font-mono text-white flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
            <div>Loading details...</div>
        </div>
    );

    if (error || !university) return (
        <div className="min-h-screen bg-gray-900 font-mono text-white p-6">
            <div className="text-red-400 mb-4">{error || 'University not found'}</div>
            <button onClick={() => navigate('/admin/universities')} className="text-blue-400 hover:underline">
                &larr; Back to Catalog
            </button>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-900 font-mono text-white p-6">
            <div className="max-w-6xl mx-auto">
                <button onClick={() => navigate('/admin/universities')} className="mb-4 text-gray-400 hover:text-white flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                    Back to Catalog
                </button>

                {/* University Header */}
                <div className="bg-gray-800 border border-gray-700 rounded-lg shadow p-6 mb-6">
                    <h1 className="text-3xl font-bold text-white mb-2">{university.name}</h1>
                    <div className="flex items-center gap-4 text-gray-300">
                        <span className="flex items-center">
                            <span className="mr-2">🌍</span> {university.country}
                        </span>
                        {university.website && (
                            <a href={university.website} target="_blank" rel="noreferrer" className="text-blue-400 hover:underline flex items-center">
                                <span className="mr-2">🔗</span> Website
                            </a>
                        )}
                    </div>
                </div>

                {/* Programs Section */}
                <div className="mb-8">
                    <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                        <span className="mr-2">📚</span> Programs ({programs.length})
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {programs.map(p => {
                            // Filter intakes for this program
                            const progIntakes = intakes.filter(i => i.program_id === p.id);

                            return (
                                <div
                                    key={p.id}
                                    onClick={() => navigate(`/admin/programs/${p.id}`)}
                                    className="bg-gray-800 border border-gray-700 rounded-lg p-4 hover:border-blue-500 transition-all cursor-pointer group relative"
                                >
                                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                    </div>
                                    <div className="flex justify-between items-start mb-2 pr-6">
                                        <h3 className="font-bold text-lg text-blue-300 group-hover:text-blue-200">{p.name}</h3>
                                        <span className="text-xs bg-gray-700 px-2 py-1 rounded text-gray-300 whitespace-nowrap">{p.level}</span>
                                    </div>
                                    <div className="text-sm text-gray-400 mb-1">{p.discipline}</div>
                                    {p.course_of_study && <div className="text-xs text-blue-400 mb-2">{p.course_of_study}</div>}

                                    {/* New Fields Display */}
                                    <div className="space-y-1 text-xs text-gray-500 mt-3 pt-3 border-t border-gray-700">
                                        {p.tuition && (
                                            <div className="text-green-400 font-medium">
                                                💰 {p.tuition} {p.currency || 'EUR'} <span className="text-gray-500 font-normal">({p.tuition_fee_type || 'Total'})</span>
                                            </div>
                                        )}
                                        {p.semesters && <div>📅 Semesters: {p.semesters}</div>}

                                        {/* Intakes Preview */}
                                        {progIntakes.length > 0 && (
                                            <div className="mt-2 pt-2 border-t border-gray-700/50">
                                                <div className="font-medium text-gray-400 mb-1">Upcoming Intakes:</div>
                                                <div className="flex flex-wrap gap-1">
                                                    {progIntakes.slice(0, 3).map(i => (
                                                        <span key={i.id} className="bg-blue-900/30 text-blue-300 px-1.5 py-0.5 rounded border border-blue-800/50">
                                                            {i.term}
                                                        </span>
                                                    ))}
                                                    {progIntakes.length > 3 && <span className="text-gray-500">+{progIntakes.length - 3} more</span>}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        {programs.length === 0 && (
                            <div className="col-span-full text-center py-8 text-gray-500 bg-gray-800/50 rounded-lg border border-gray-700 border-dashed">
                                No programs found for this university.
                            </div>
                        )}
                    </div>
                </div>

                {/* Intakes Section */}
                <div>
                    <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                        <span className="mr-2">📅</span> Upcoming Intakes
                    </h2>
                    <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-gray-900/50">
                                <tr>
                                    <th className="p-3 border-b border-gray-700 font-medium text-gray-400">Program</th>
                                    <th className="p-3 border-b border-gray-700 font-medium text-gray-400">Term</th>
                                    <th className="p-3 border-b border-gray-700 font-medium text-gray-400">Apply By</th>
                                    <th className="p-3 border-b border-gray-700 font-medium text-gray-400">Start Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {intakes.map(i => (
                                    <tr key={i.id} className="hover:bg-gray-700/50">
                                        <td className="p-3 border-b border-gray-700 font-medium">{i.programs?.name}</td>
                                        <td className="p-3 border-b border-gray-700">{i.term}</td>
                                        <td className="p-3 border-b border-gray-700 text-yellow-400">{i.apply_close}</td>
                                        <td className="p-3 border-b border-gray-700">{i.start_date}</td>
                                    </tr>
                                ))}
                                {intakes.length === 0 && (
                                    <tr>
                                        <td colSpan={4} className="p-8 text-center text-gray-500">No upcoming intakes found.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default UniversityDetailPage;
